import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import { NGROK } from "../../../../APIs";
import axios from "axios";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";

const BLUE = "#3b83f6";
const RESOURCE_ROLE_FILTERS = new Set([
  "virtual_machine",
  "storage",
  "database",
  "kubernetes",
]);
const EMPTY_RESOURCE_ROLES = {
  virtual_machine: [],
  storage: [],
  database: [],
  kubernetes: [],
};

const sortResourceRoles = (roles, isPreferred) =>
  [...roles].sort((first, second) => {
    const firstName = first?.roleName || "";
    const secondName = second?.roleName || "";
    const priorityDifference =
      Number(!isPreferred(firstName)) - Number(!isPreferred(secondName));
    return (
      priorityDifference ||
      firstName.localeCompare(secondName, undefined, { sensitivity: "base" })
    );
  });

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const RolesListSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
    <div className="role-dashboard-grid">
      <div className="role-panel">
        <div className="role-panel-header">
          <div>
            <Sk className="h-4 w-24 rounded" />
            <Sk className="h-3 w-52 rounded mt-2" />
          </div>
          <Sk className="h-6 w-9 rounded-full" />
        </div>

        <div className="role-search-shell">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="role-list">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="role-row"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="role-panel">
        <div className="role-detail-header">
          <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <Sk className="h-5 w-2/3 rounded" />
            <Sk className="h-4 w-1/3 rounded mt-2" />
          </div>
        </div>

        <div className="role-detail-tabs">
          <Sk className="h-8 w-20 rounded-lg" />
          <Sk className="h-8 w-28 rounded-lg" />
        </div>

        <div className="role-detail-content role-info-stack">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="role-info-card">
              <Sk className="h-3 w-24 rounded" />
              <Sk className="h-4 w-full rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const GCPGenericRoles = ({ latestTenant, projectId, roleFilter = "all" }) => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [resourceRoles, setResourceRoles] = useState(EMPTY_RESOURCE_ROLES);
  const [resourceRolesLoading, setResourceRolesLoading] = useState(false);
  const [filter, setFilter] = useState(roleFilter);
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [isrolesLoading, setIsrolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef({ pageNo: 0, searchString: "" });

  const filterRoles = useCallback((allRoles, f) => {
    if (f === "default") return allRoles?.filter((role) => !role.isCustom);
    if (f === "custom") return allRoles?.filter((role) => role.isCustom);
    return allRoles;
  }, []);

  const filteredRoles = useMemo(() => {
    if (RESOURCE_ROLE_FILTERS.has(filter)) {
      const query = searchString.trim().toLowerCase();
      return (resourceRoles[filter] || []).filter((role) => {
        if (!role?.roleName) return false;
        if (!query) return true;
        return (
          role.roleName.toLowerCase().includes(query) ||
          role.roleId?.toLowerCase().includes(query)
        );
      });
    }
    const base = (roles || []).filter((r) => r?.roleName);
    return filterRoles(base, filter);
  }, [roles, resourceRoles, filter, searchString, filterRoles]);

  useEffect(() => {
    if (filteredRoles?.length > 0 && !selectedRole) {
      setSelectedRole(filteredRoles[0]);
    } else if (filteredRoles?.length > 0 && selectedRole) {
      const stillExists = filteredRoles.some(
        (r) => (r.roleId || r.id) === (selectedRole.roleId || selectedRole.id)
      );
      if (!stillExists) {
        setSelectedRole(filteredRoles[0]);
      }
    } else if (filteredRoles?.length === 0) {
      setSelectedRole(null);
    }
  }, [filteredRoles]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSelectedDetailsTab(0);
  };

  const handleSearchSubmit = () => {
    if (!RESOURCE_ROLE_FILTERS.has(filter)) {
      setRoles([]);
    }
    setPageNo(1);
    setReachedEnd(false);
    setSelectedRole(null);
    setSelectedDetailsTab(0);
    lastFetchParamsRef.current = { pageNo: 0, searchString: "" };
    setSearchString(search.trim());
  };

  const handleRoleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);

    if (!value || value.trim() === "") {
      if (!RESOURCE_ROLE_FILTERS.has(filter)) {
        setRoles([]);
      }
      setPageNo(1);
      setReachedEnd(false);
      setSelectedRole(null);
      setSelectedDetailsTab(0);
      lastFetchParamsRef.current = { pageNo: 0, searchString: "" };
      setSearchString("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedDetailsTab(0);
  };

  const handlePublishToggle = async (event, roleId) => {
    const isManagedRole = event?.target?.checked;

    updateManagedRole(roleId, isManagedRole);

    try {
      const response = await axios.put(
        `${NGROK}/api/gcp/resource/publishRole?tenantName=${encodeURIComponent(
          latestTenant
        )}&projectId=${encodeURIComponent(projectId)}&roleId=${encodeURIComponent(
          roleId
        )}`
      );
      if (response.data !== true) {
        throw new Error("Role publish update failed");
      }
    } catch (e) {
      updateManagedRole(roleId, !isManagedRole);
    }
  };

  const updateManagedRole = (roleId, isManagedRole) => {
    const updateRoles = (roleList) =>
      roleList.map((role) =>
        role.roleId === roleId ? { ...role, isManagedRole } : role
      );
    setRoles(updateRoles);
    setResourceRoles((previous) => ({
      virtual_machine: updateRoles(previous.virtual_machine),
      storage: updateRoles(previous.storage),
      database: updateRoles(previous.database),
      kubernetes: updateRoles(previous.kubernetes),
    }));
  };

  const getAllRoles = async (page, searchStr) => {
    let url = `${NGROK}/api/gcp/resource/get-roles?tenantName=${latestTenant}&projectId=${projectId}&page=${page}&size=25`;
    if (searchStr && searchStr.length > 0) {
      url += `&searchString=${encodeURIComponent(searchStr)}`;
    }
    const response = await axios.get(url);
    if (response.status === 200) return response.data;
    return [];
  };

  const fetchResourceRoles = async () => {
    setResourceRolesLoading(true);
    try {
      const response = await axios.get(
        `${NGROK}/api/gcp/resource/getGcpResourcesRoles`,
        {
          params: {
            tenantName: latestTenant,
            projectId,
          },
        }
      );
      setResourceRoles({
        virtual_machine: sortResourceRoles(
          response.data?.virtual_machine || [],
          (name) => name.toLowerCase().includes("compute")
        ),
        storage: sortResourceRoles(
          response.data?.storage || [],
          (name) => name.toLowerCase().includes("storage")
        ),
        database: sortResourceRoles(
          response.data?.database || [],
          (name) => name.toLowerCase().startsWith("cloud sql")
        ),
        kubernetes: sortResourceRoles(
          response.data?.kubernetes || [],
          (name) => name.toLowerCase().includes("cluster")
        ),
      });
    } catch (error) {
      setResourceRoles(EMPTY_RESOURCE_ROLES);
    } finally {
      setResourceRolesLoading(false);
    }
  };

  const fetchData = async (page, searchStr, isInitialLoad = false) => {
    if (!latestTenant || !projectId) {
      setRoles([]);
      setSelectedRole(null);
      setLoading(false);
      setIsrolesLoading(false);
      setReachedEnd(true);
      return;
    }

    if (
      isFetchingRef.current &&
      lastFetchParamsRef.current.pageNo === page &&
      lastFetchParamsRef.current.searchString === searchStr
    ) {
      return;
    }

    if (
      !isInitialLoad &&
      page === lastFetchParamsRef.current.pageNo &&
      searchStr === lastFetchParamsRef.current.searchString
    ) {
      return;
    }

    isFetchingRef.current = true;
    lastFetchParamsRef.current = { pageNo: page, searchString: searchStr };

    try {
      const rolesData = await getAllRoles(page, searchStr);

      if (!rolesData || rolesData.length === 0) {
        setReachedEnd(true);
      } else {
        setReachedEnd(false);
      }

      if (page > 1) {
        setRoles((prevRoles) => [...prevRoles, ...rolesData]);
      } else {
        setRoles(rolesData);
      }

      setLoading(false);
      setIsrolesLoading(false);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setLoading(false);
      setIsrolesLoading(false);
      setReachedEnd(true);
    } finally {
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (projectId != null && latestTenant) {
      setLoading(true);
      setRoles([]);
      setPageNo(1);
      setReachedEnd(false);
      setSelectedRole(null);
      setSearch("");
      setSearchString("");
      setResourceRoles(EMPTY_RESOURCE_ROLES);
      lastFetchParamsRef.current = { pageNo: 0, searchString: "" };
      fetchData(1, "", true);
      fetchResourceRoles();
    } else {
      setRoles([]);
      setResourceRoles(EMPTY_RESOURCE_ROLES);
      setSelectedRole(null);
      setLoading(false);
      setIsrolesLoading(false);
      setReachedEnd(true);
    }
  }, [latestTenant, projectId]);

  useEffect(() => {
    if (projectId != null && latestTenant) {
      if (lastFetchParamsRef.current.searchString !== searchString || searchString === "") {
        fetchData(1, searchString, false);
      }
    }
  }, [searchString]);

  useEffect(() => {
    if (pageNo > 1 && projectId != null && latestTenant && !reachedEnd) {
      fetchData(pageNo, searchString, false);
    }
  }, [pageNo]);

  useEffect(() => {
    handleFilterChange(roleFilter);
  }, [roleFilter]);

  const loadMoreRoles = useCallback(() => {
    if (
      RESOURCE_ROLE_FILTERS.has(filter) ||
      reachedEnd ||
      isrolesLoading ||
      isFetchingRef.current
    ) return;
    setIsrolesLoading(true);
    setPageNo((prevPage) => prevPage + 1);
  }, [filter, reachedEnd, isrolesLoading]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop <= clientHeight + 100 &&
        !reachedEnd &&
        !isrolesLoading &&
        !isFetchingRef.current
      ) {
        loadMoreRoles();
      }
    },
    [loadMoreRoles, reachedEnd, isrolesLoading]
  );

  const selectedKey = selectedRole?.roleId || selectedRole?.id;

  return (
    <>
      {loading || (RESOURCE_ROLE_FILTERS.has(filter) && resourceRolesLoading) ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
          <div className="role-dashboard-grid">
            <div className="role-panel">
              <div className="role-panel-header">
                <div>
                  <div className="role-panel-title">Roles</div>
                  <div className="role-panel-subtitle">Select a role to inspect permissions</div>
                </div>
                <span className="role-count-pill">{filteredRoles?.length || 0}</span>
              </div>

              <div className="role-search-shell">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="role-search-input"
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="role-search-icon"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="role-list" onScroll={handleScroll}>
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const roleKey = role.roleId || role.id || index;
                    const isSelected = roleKey === selectedKey;

                    return (
                      <div
                        key={roleKey}
                        onClick={() => handleRoleClick(role)}
                        className={`role-row ${isSelected ? "active" : ""}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="role-avatar">
                            <SecurityIcon sx={{ color: BLUE, fontSize: 20 }} />
                          </div>
                          <div className="role-row-text">
                            <p className="role-row-title">{role.roleName}</p>
                            <p className="role-row-subtitle">
                              {role.isCustom ? "Custom Role" : "Predefined Role"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <TailwindSwitch
                            isDisabled={false}
                            action={(e, data) => handlePublishToggle(e, data.roleId)}
                            data={role}
                            isChecked={role.isManagedRole}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="role-empty-state">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}

                {isrolesLoading && (
                  <div className="flex justify-center py-4">
                    <div
                      className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: BLUE, borderTopColor: "transparent" }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="role-panel">
              {selectedRole ? (
                <>
                  <div className="role-detail-header">
                    <div className="role-avatar">
                      <SecurityIcon sx={{ color: BLUE, fontSize: 22 }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="role-detail-title">{selectedRole?.roleName}</h3>
                      <p className="role-detail-subtitle">
                        {selectedRole?.isCustom ? "Custom Role" : "Predefined Role"}
                      </p>
                    </div>
                  </div>

                  <div className="role-detail-tabs">
                    <button
                      onClick={() => setSelectedDetailsTab(0)}
                      className={`role-detail-tab ${selectedDetailsTab === 0 ? "active" : ""}`}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                      Info
                    </button>
                    <button
                      onClick={() => setSelectedDetailsTab(1)}
                      className={`role-detail-tab ${selectedDetailsTab === 1 ? "active" : ""}`}
                    >
                      <VpnKeyIcon fontSize="small" />
                      Permissions
                    </button>
                  </div>

                  <div className="role-detail-content">
                    {selectedDetailsTab === 0 && (
                      <div className="role-info-stack">
                        <div className="role-info-card">
                          <div className="role-info-label">Role Name</div>
                          <div className="role-info-value">{selectedRole?.roleName || "—"}</div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Role ID</div>
                          <div className="role-info-value font-mono">{selectedRole?.roleId || "—"}</div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Description</div>
                          <div className="role-info-value">{selectedRole?.description || "—"}</div>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Attached Permissions
                          </span>
                          <span
                            className="px-2 py-0.5 text-xs font-semibold rounded-full"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)", color: BLUE }}
                          >
                            {selectedRole?.permissions?.length || 0}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {selectedRole?.permissions?.length > 0 ? (
                            selectedRole.permissions.map((perm, idx) => (
                              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: BLUE }} />
                                <span className="text-sm text-foreground/85 font-mono break-all">{perm}</span>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                              <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
                              <p className="text-sm mt-2">No permissions attached</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="role-empty-state">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GCPGenericRoles;
